/* eslint-disable react-hooks/exhaustive-deps */
import ArrowsUpDownIcon from "@heroicons/react/24/solid/ArrowsUpDownIcon";
import { useNavigate } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { TrashIcon, PencilIcon, WrenchIcon } from "@heroicons/react/24/outline";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import * as XLSX from "xlsx";
import { templateEditHeaderRoute } from "~/pages/all-routes";
import { deleteTemplates } from "~/services/supervisor.services";

import { SubmitButton as Button } from "~/components/form/submit-button";
import { toast } from "react-toastify";
interface ITemplatesTableProps {
  data: any;
  onEdit?: (data: any) => void;
}
type SelectedRowIds = Record<string, boolean>;
const TemplatesTable = forwardRef(
  ({ data, onEdit }: ITemplatesTableProps, ref) => {

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate({ from: "/" });
    const [filteredData, setFilteredData] = useState<any[]>([]);
    // const [filters, setFilters] = useState<Record<string, string[]>>({});
    const [selectedRowIds, setSelectedRowIds] = useState<SelectedRowIds>({});
    const [showSuccessModalReload, setShowSuccessModalReload] = useState(false);
    const [SuccessMessage, setSuccessMessage] = useState("");
    const tableData = useMemo(() => data, [data]);
    const [sortConfig, setSortConfig] = useState<{
      key: string;
      direction: "asc" | "desc";
    }>({
      key: "",
      direction: "asc",
    });

    const requestSort = (key: string) => {
      let direction: "asc" | "desc" = "asc";
      if (
        sortConfig &&
        sortConfig.key === key &&
        sortConfig.direction === "asc"
      ) {
        direction = "desc";
      }
      setSortConfig({ key, direction });
    };

    // Define DeleteSelectedRows function
    const DeleteSelectedRows = async () => {
      const idsToDelete = Object.keys(selectedRowIds);
      if (idsToDelete.length === 0) {
        alert("No rows selected for deletion.");
        return; // Exit the function early
      }

      const confirmDelete = window.confirm(
        "Are you sure you want to delete these records?"
      );
      if (confirmDelete) {
        handleDeleteAll();
      }
    };

    useImperativeHandle(ref, () => ({
      DeleteSelectedRows,
    }));

    // useEffect(() => {
    //   if (filters != null) {
    //     let newData = data;
    //     Object.keys(filters).forEach((key) => {
    //       newData = newData.filter((item: { [x: string]: string }) =>
    //         filters[key].includes(item[key])
    //       );
    //     });
    //     setFilteredData(newData);
    //   } else {
    //     setFilteredData(data);
    //   }
    // }, [filters, data]);
    const handleRowsPerPageChange = (
      event: React.ChangeEvent<HTMLSelectElement>
    ) => {
      setRowsPerPage(Number(event.target.value));
      setCurrentPage(0); // Reset to first page
    };

    const sortedData = useMemo(() => {
      if (!sortConfig.key) return filteredData;
      return [...filteredData].sort((a: any, b: any) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }, [filteredData, sortConfig]);

    const paginatedData = useMemo(() => {
      const start = currentPage * rowsPerPage;
      const end = start + rowsPerPage;
      return sortedData.slice(start, end);
    }, [sortedData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(sortedData.length / rowsPerPage);
    // Handle page change
    const handlePageChange = (newPage: number) => {
      setCurrentPage(newPage);
    };
    const columnHelper = createColumnHelper<any[]>();
    const columnHeaders: Record<string, string> = {
      modelno: "Model No",
      modeldesc: "Model Desc",
      status: "Status",
      hint_1: "File Expression",
      hint_2:"File Type",
      documenttype: "Document Type",
      priority: "Priority",
      assignedtouserid: "Created User",
      createdate: "Create Date",
    };

    const [searchInputs, setSearchInputs] = useState<Record<string, string>>({
      modelno: "",
      modeldesc: "",
      status: "",
      hint_1: "",
      hint_2: "",
      documenttype: "",
      priority: "",
      assignedtouserid: "",
      createdate: "",
    });

    // Debounce function for search input
    // eslint-disable-next-line @typescript-eslint/ban-types
    const debounce = (func: Function, delay: number) => {
      let timer: NodeJS.Timeout;
      return (...args: any[]) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
      };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetSearch = useCallback(
      debounce((key: string, value: string) => {
        setSearchInputs((prev) => ({
          ...prev,
          [key]: value,
        }));
      }, 0), // Adjusted delay for better user experience
      []
    );

    const handleInputChange = useCallback(
      (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        debouncedSetSearch(key, value);
      },
      [debouncedSetSearch]
    );

    // Filtered data based on search inputs
    useEffect(() => {
      const filtered = data.filter(
        (row: { [x: string]: { toString: () => string } }) =>
          Object.entries(searchInputs).every(
            ([key, searchTerm]) =>
              !searchTerm ||
              row[key]
                ?.toString()
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
          )
      );
      setFilteredData(filtered);
      setCurrentPage(0); // Reset to first page on search change
    }, [searchInputs, data]);

    const handleDelete = async (modelno: string) => {
      try {
        const response = await deleteTemplates(modelno);

        if (response.status) {
          setSuccessMessage("Template deleted successfully");
          setShowSuccessModalReload(true);
          setTimeout(() => {
            setShowSuccessModalReload(false);
          }, 3000);
          const updatedData = tableData.filter(
            (template: { modelno: any }) => template.modelno !== modelno
          );
          setFilteredData(updatedData);
        } else {
          console.error("Failed to delete Models");
        }
      } catch (error) {
        console.error("Error deleting Models", error);
      }
    };

    const handleDeleteAll = async () => {
      const idsToDelete = Object.keys(selectedRowIds);
      const intKeys = idsToDelete.map((key) => parseInt(key, 10));
      // Check if idsToDelete is empty
      if (idsToDelete.length === 0) {
        alert("No rows selected for deletion.");
        return; // Exit the function early
      }
      // Perform deletions and wait for all to complete
      const deletePromises = idsToDelete.map((key) => deleteTemplates(key));
      const responses = await Promise.all(deletePromises);

      const allSuccessful = responses.every((response) => response.status);

      if (allSuccessful) {
        const updatedData = tableData.filter(
          (template: { modelno: any }) => !intKeys.includes(template.modelno)
        );
        ``;
        setFilteredData(updatedData);

        // Clear row selection
        tableObj.setRowSelection({});
        //alert('Record(S) deleted successfully');
        setSuccessMessage("Record(S) deleted successfully");
        setShowSuccessModalReload(true);
        setTimeout(() => {
          setShowSuccessModalReload(false);
        }, 0);
        console.log("Rows deleted with IDs:", idsToDelete.join(", "));
      } else {
        console.log("Some deletions failed.");
        alert("Some deletions failed.");
      }

      setSelectedRowIds({});
      window.location.reload();
      console.log("Deletion process completed for selected rows.");
    };

    const handleExportToExcel = () => {
      const keys = Object.keys(selectedRowIds);
      const intKeys = keys.map((key) => parseInt(key, 10));
      const selectedRows = data.filter((row: any) =>
        intKeys.includes(row.modelno)
      );
      console.log(selectedRows);

      if (selectedRows.length === 0) {
        toast.error("No rows selected for export.", {
          theme: "colored",
        });
        return;
      }

      const formattedRows = selectedRows.map((row: any) => ({
        "Model No": row.modelno,
        "Model Desc": row.modeldesc,
        Status: row.status,
        "File Expression": row.hint_1, // Rename hint_1 to FileExpression
        "File Type": row.hint_2,
        "Document Type": row.documenttype,
        Priority: row.priority,
        "Created User": row.assignedtouserid, // Rename assignedToUserId to createdUser
        "Create Date": row.createdate,
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedRows);

      // Calculate column widths
      const columnWidths: number[] = formattedRows.reduce(
        (acc: number[], row: Record<string, any>) => {
          Object.keys(row).forEach((key, index) => {
            const cellValue = row[key] ? row[key].toString() : "";
            acc[index] = Math.max(acc[index] || 0, cellValue.length);
          });
          return acc;
        },
        []
      );
      columnWidths[3] = 20;
      columnWidths[4] = 10;

      // Add a little extra space to each column width
      const widthSettings = columnWidths.map((width) => ({ wch: width + 5 }));

      worksheet["!cols"] = widthSettings; // Apply the widths to the worksheet

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Output data");

      XLSX.writeFile(workbook, "ModelList.xlsx");
    };
    const columns = useMemo(() => {
      const createHeader = (key: string) => {
        // Get the display name from the mapping or use the key itself as fallback
        const headerName = columnHeaders[key] || key;

        return (
          <div className="p-2">
            <div className="flex flex-col gap-2 justify-center">
              <div className="flex gap-2 justify-center">{headerName}</div>
              <div className="flex items-center justify-center gap-2">
                <input
                  type="text"
                  value={searchInputs[key] || ""}
                  onChange={handleInputChange(key)}
                  //placeholder={`Search ${headerName}`}
                  className="border border-gray-300 rounded"
                />
                <button onClick={() => requestSort(key)}>
                  <ArrowsUpDownIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        );
      };
      const headerAction = <div className="p-2">Action</div>;
      const headerSelect = (
        <input
          type="checkbox"
          checked={Object.keys(selectedRowIds).length === data.length} // Select all if all are selected
          onChange={() => {
            console.log("checked");
            const allSelected =
              Object.keys(selectedRowIds).length === data.length;
            const newSelectedIds: SelectedRowIds = {};

            if (!allSelected) {
              data.forEach((row: { modelno: string | number }) => {
                newSelectedIds[row.modelno] = true; // Assuming row.id is unique
                console.log(newSelectedIds);
              });
            }
            setSelectedRowIds(newSelectedIds);
          }}
        />
      );

      return [
        {
          id: "select",
          header: headerSelect,
          // eslint-disable-nex t-line @typescript-eslint/no-unused-vars
          cell: ({ row }: { row: any }) => (
            <input
              type="checkbox"
              checked={selectedRowIds[(row.original as any).modelno] || false}
              onChange={() => {
                setSelectedRowIds((prev) => {
                  const newSelectedRowIds = { ...prev };
                  if (newSelectedRowIds[(row.original as any).modelno]) {
                    delete newSelectedRowIds[(row.original as any).modelno];
                  } else {
                    newSelectedRowIds[(row.original as any).modelno] = true;
                  }
                  return newSelectedRowIds;
                });
              }}
            />
          ),
        },
        {
          id: "action",
          header: headerAction,
          cell: (props: any) => (
            <div className="flex items-center justify-center gap-2 cursor-pointer">
              <div
                onClick={() => {
                  navigate({
                    to: templateEditHeaderRoute.fullPath,
                    search: {
                      id: (props.row.original as any).modelno,
                    },
                  });
                }}
                data-tooltip-content="Configure"
                data-tooltip-id="ConfigureTooltip"
              >
                <ReactTooltip id="ConfigureTooltip" />
                <WrenchIcon className="h-4 w-4 text-blue-500" />
              </div>
              <div
                onClick={() => {
                  console.log(props.row.original);
                  onEdit?.(props.row.original);
                }}
                aria-label="Edit"
                data-tooltip-content="Edit"
                data-tooltip-id="EditTooltip"
              >
                <ReactTooltip id="EditTooltip" />
                <PencilIcon className="h-4 w-4 text-blue-500" />
              </div>
              <div
                onClick={async () => {
                  const confirmDelete = window.confirm(
                    "Are you sure you want to delete the record?"
                  );
                  if (confirmDelete) {
                    await handleDelete((props.row.original as any).modelno);
                  }
                }}
                data-tooltip-content="Delete"
                data-tooltip-id="deleteTooltip"
              >
                <ReactTooltip id="deleteTooltip" />
                <TrashIcon className="h-4 w-4 text-red-500" />
              </div>
            </div>
          ),
        },
        ...[
          "modelno",
          "modeldesc",
          "status",
          "hint_1",
          "hint_2",
          "documenttype",
          "priority",
          "assignedtouserid",
          "createdate",
        ].map((key) =>
          columnHelper.accessor(key, {
            header: createHeader(key) as any,
            cell: (props: any) => {
              const value = props.getValue();
              // Render boolean values as "Yes" or "No"
              if (typeof value === "boolean") {
                return <div>{value ? "Yes" : "No"}</div>;
              }
              return <div>{value}</div>;
            },
          })
        ),
      ];
    }, [
      selectedRowIds,
      tableData,
      navigate,
      columnHeaders,
      searchInputs,
      handleInputChange,
      columnHelper,
    ]);

    // const columns = useMemo(
    //   () => [
    //     columnHelper.accessor("select", {
    //       header: () => (
    //         <input
    //           type="checkbox"
    //           checked={Object.keys(selectedRowIds).length === data.length} // Select all if all are selected
    //           onChange={() => {
    //             console.log("checked")
    //             const allSelected =
    //               Object.keys(selectedRowIds).length === data.length;
    //             const newSelectedIds: SelectedRowIds = {};

    //             if (!allSelected) {
    //               data.forEach((row: { modelno: string | number; }) => {
    //                 newSelectedIds[row.modelno] = true; // Assuming row.id is unique
    //                 console.log(newSelectedIds)
    //               });
    //             }
    //             setSelectedRowIds(newSelectedIds);
    //           }}
    //         />
    //       ),
    //       cell: ({ row }) => (
    //         <input
    //           type="checkbox"
    //           checked={selectedRowIds[(row.original as any).modelno] || false}
    //           onChange={() => {
    //             setSelectedRowIds((prev) => {
    //               const newSelectedRowIds = { ...prev };
    //               if (newSelectedRowIds[(row.original as any).modelno]) {
    //                 delete newSelectedRowIds[(row.original as any).modelno];
    //               } else {
    //                 newSelectedRowIds[(row.original as any).modelno] = true;
    //               }
    //               return newSelectedRowIds;
    //             });
    //           }}
    //         />
    //       ),
    //     }),
    //     columnHelper.accessor("actions", {
    //       header: () => (
    //         <div className="p-2">
    //           <div className="flex gap-2 justify-center">
    //             <div>Actions</div>
    //           </div>
    //         </div>
    //       ),
    //       cell: (props) => (
    //         <div className="flex flex-row-reverse gap-2">
    //           <button
    //             // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    //             onClick={async () =>
    //               handleDelete((props.row.original as any).modelno)
    //             }
    //             aria-label="Delete"
    //             data-tooltip-content="Delete"
    //             data-tooltip-id="deleteTooltip"
    //           >
    //             <img
    //               src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxZW0iIGhlaWdodD0iMWVtIiB2aWV3Qm94PSIwIDAgMjQgMjQiPgoJPHBhdGggZmlsbD0iI2Y3N2QyYiIgZD0iTTcgMjFxLS44MjUgMC0xLjQxMi0uNTg3VDUgMTlWNnEtLjQyNSAwLS43MTItLjI4OFQ0IDV0LjI4OC0uNzEyVDUgNGg0cTAtLjQyNS4yODgtLjcxMlQxMCAzaDRxLjQyNSAwIC43MTMuMjg4VDE1IDRoNHEuNDI1IDAgLjcxMy4yODhUMjAgNXQtLjI4OC43MTNUMTkgNnYxM3EwIC44MjUtLjU4NyAxLjQxM1QxNyAyMXptNS03LjFsMS45IDEuOXEuMjc1LjI3NS43LjI3NXQuNy0uMjc1dC4yNzUtLjd0LS4yNzUtLjdsLTEuOS0xLjlsMS45LTEuOXEuMjc1LS4yNzUuMjc1LS43dC0uMjc1LS43dC0uNy0uMjc1dC0uNy4yNzVMMTIgMTEuMWwtMS45LTEuOXEtLjI3NS0uMjc1LS43LS4yNzV0LS43LjI3NXQtLjI3NS43dC4yNzUuN2wxLjkgMS45bC0xLjkgMS45cS0uMjc1LjI3NS0uMjc1Ljd0LjI3NS43dC43LjI3NXQuNy0uMjc1eiIgLz4KPC9zdmc+"
    //               alt="Delete"
    //               width="20"
    //               height="20"
    //             />
    //           </button>
    //           <ReactTooltip id="deleteTooltip" />
    //           <button
    //             onClick={() => {
    //               console.log(props.row.original);
    //               onEdit?.(props.row.original);
    //             }}
    //             aria-label="Edit"
    //             data-tooltip-content="Edit"
    //             data-tooltip-id="EditTooltip"
    //           >
    //             <img
    //               src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxZW0iIGhlaWdodD0iMWVtIiB2aWV3Qm94PSIwIDAgMjQgMjQiPgoJPHBhdGggZmlsbD0iI2Y3N2QyYiIgZD0iTTkgMTV2LTQuMjVsOS4xNzUtOS4xNzVxLjMtLjMuNjc1LS40NXQuNzUtLjE1cS40IDAgLjc2My4xNXQuNjYyLjQ1TDIyLjQyNSAzcS4yNzUuMy40MjUuNjYzVDIzIDQuNHQtLjEzNy43Mzh0LS40MzguNjYyTDEzLjI1IDE1em0xMC42LTkuMmwxLjQyNS0xLjRsLTEuNC0xLjRMMTguMiA0LjR6TTUgMjFxLS44MjUgMC0xLjQxMi0uNTg3VDMgMTlWNXEwLS44MjUuNTg4LTEuNDEyVDUgM2g4LjkyNUw3IDkuOTI1VjE3aDcuMDVMMjEgMTAuMDVWMTlxMCAuODI1LS41ODcgMS40MTNUMTkgMjF6IiAvPgo8L3N2Zz4="
    //               alt="Edit"
    //               width="20"
    //               height="20"
    //             />
    //           </button>
    //           <ReactTooltip id="EditTooltip" />
    //           <button
    //             onClick={() => {
    //               navigate({
    //                 to: templateEditHeaderRoute.fullPath,
    //                 search: {
    //                   id: (props.row.original as any).modelno,
    //                 },
    //               });
    //             }}
    //             aria-label="Configure"
    //             data-tooltip-content="Configure"
    //             data-tooltip-id="ConfigureTooltip"
    //           >
    //             <img
    //               src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxZW0iIGhlaWdodD0iMWVtIiB2aWV3Qm94PSIwIDAgMjQgMjQiPgoJPHBhdGggZmlsbD0iI2Y3N2QyYiIgZD0ibTkuMjUgMjJsLS40LTMuMnEtLjMyNS0uMTI1LS42MTItLjN0LS41NjMtLjM3NUw0LjcgMTkuMzc1bC0yLjc1LTQuNzVsMi41NzUtMS45NVE0LjUgMTIuNSA0LjUgMTIuMzM4di0uNjc1cTAtLjE2My4wMjUtLjMzOEwxLjk1IDkuMzc1bDIuNzUtNC43NWwyLjk3NSAxLjI1cS4yNzUtLjIuNTc1LS4zNzV0LjYtLjNsLjQtMy4yaDUuNWwuNCAzLjJxLjMyNS4xMjUuNjEzLjN0LjU2Mi4zNzVsMi45NzUtMS4yNWwyLjc1IDQuNzVsLTEuODUgMS40cS0uNTUtLjI3NS0xLjEyNS0uNDYydC0xLjIyNS0uMjYzbDEuNTc1LTEuMmwtLjk3NS0xLjdsLTIuNDc1IDEuMDVxLS41NS0uNTc1LTEuMjEzLS45NjJ0LTEuNDM3LS41ODhMMTMgNGgtMS45NzVsLS4zNSAyLjY1cS0uNzc1LjItMS40MzcuNTg4dC0xLjIxMy45MzdMNS41NSA3LjE1bC0uOTc1IDEuN2wyLjE1IDEuNnEtLjEyNS4zNzUtLjE3NS43NXQtLjA1LjhxMCAuNC4wNS43NzV0LjE3NS43NWwtMi4xNSAxLjYyNWwuOTc1IDEuN2wyLjQ3NS0xLjA1cS40MjUuNDI1LjkxMy43NjN0MS4wNjIuNTYycS4wMjUgMS40MjUuNTg4IDIuNjc1VDEyLjEgMjJ6bTEuMDI1LTYuOTc1cS4xNS0uNS4zNjMtLjk2M3QuNDg3LS44ODdxLS4yNzUtLjItLjQyNS0uNTEzVDEwLjU1IDEycTAtLjYyNS40MzctMS4wNjJ0MS4wNjMtLjQzOHEuMzUgMCAuNjc1LjE2M3QuNTI1LjQzN3EuNDI1LS4yNzUuODc1LS40ODh0Ljk1LS4zMzdxLS40NS0uOC0xLjI1LTEuMjg4VDEyLjA1IDguNXEtMS40NzUgMC0yLjQ4OCAxLjAyNVQ4LjU1IDEycTAgLjk1LjQ2MyAxLjc2M3QxLjI2MiAxLjI2Mk0xNyAyMGwzLTNsLTMtM2wtLjcuN2wxLjggMS44SDE0djFoNC4wNzVMMTYuMyAxOS4zem0wIDJxLTIuMDc1IDAtMy41MzctMS40NjNUMTIgMTd0MS40NjMtMy41MzdUMTcgMTJ0My41MzggMS40NjNUMjIgMTd0LTEuNDYzIDMuNTM4VDE3IDIyIiAvPgo8L3N2Zz4="
    //               alt="Configure"
    //               width="20"
    //               height="20"
    //             />
    //           </button>
    //           <ReactTooltip id="ConfigureTooltip" />
    //         </div>
    //       ),
    //     }),
    //   ],
    //   [columnHelper, tableData, navigate,selectedRowIds]
    // );

    const tableObj = useReactTable({
      columns: columns as any,
      data: paginatedData,
      enableRowSelection: true,
      enableSorting: false,
      getCoreRowModel: getCoreRowModel(),
      enableMultiRowSelection: true,
      debugTable: true,
    });

    return (
      <div className="w-full ">
        <div className="flex justify-between mb-4">
          <div>
            <label>
              Rows per page:
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="ml-2 border border-gray-300 rounded"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={sortedData.length}>All</option>
              </select>
            </label>
          </div>
          <Button
            type="button"
            onClick={handleExportToExcel}
            className="bg-orange-400 text-white py-2 px-4 rounded"
          >
            Export to Excel
          </Button>
        </div>
        {showSuccessModalReload && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-4 rounded shadow-lg">
              <h2 className="text-lg font-semibold">{SuccessMessage}</h2>
            </div>
          </div>
        )}
        <div className="w-full overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              {tableObj.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th className="p-2" key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tableObj.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td className=" p-2 text-center" key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center mt-4">
          <div className="p-4">
            Showing {paginatedData.length} of {filteredData.length} entries
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
              disabled={currentPage === 0}
              className="border border-gray-300 px-4 py-2 rounded mr-2"
            >
              Previous
            </button>
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() =>
                handlePageChange(Math.min(currentPage + 1, totalPages - 1))
              }
              disabled={currentPage >= totalPages - 1}
              className="border border-gray-300 px-4 py-2 rounded ml-2"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default TemplatesTable;
